import { clamp } from './math';
import type { BlendMode, RGBA } from './types';

/**
 * Colour and opacity live on separate stop lists, exactly as Photoshop's
 * gradient editor presents them: the two rows of the strip are independent, so a
 * gradient can fade out halfway between two colours.
 */
export interface ColorStop {
  /** 0..1 along the gradient. */
  position: number;
  color: RGBA;
}

export interface OpacityStop {
  position: number;
  /** 0..1 */
  opacity: number;
}

export interface Gradient {
  id: string;
  name: string;
  /** Sorted by position; at least one entry. */
  stops: ColorStop[];
  opacityStops: OpacityStop[];
}

/** Shapes Photopea offers in the gradient tool's option bar. */
export type GradientType = 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond';

export const GRADIENT_TYPES: readonly GradientType[] = [
  'linear',
  'radial',
  'angle',
  'reflected',
  'diamond',
];

/** Must match the `GRADIENT_*` defines in `gradient.frag.glsl`. */
export const GRADIENT_TYPE_IDS: Record<GradientType, number> = {
  linear: 0,
  radial: 1,
  angle: 2,
  reflected: 3,
  diamond: 4,
};

export const GRADIENT_TYPE_LABELS: Record<GradientType, string> = {
  linear: 'Linear',
  radial: 'Radial',
  angle: 'Angle',
  reflected: 'Reflected',
  diamond: 'Diamond',
};

/** Resolution of the lookup table the shader samples. */
export const GRADIENT_LUT_SIZE = 256;

/** Everything the gradient tool's option bar holds. */
export interface GradientSettings {
  value: Gradient;
  presets: Gradient[];
  type: GradientType;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
  reverse: boolean;
  dither: boolean;
}

function sortStops<T extends { position: number }>(stops: readonly T[]): T[] {
  return [...stops].sort((a, b) => a.position - b.position);
}

function mixColor(a: RGBA, b: RGBA, t: number): RGBA {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
    a: a.a + (b.a - a.a) * t,
  };
}

/**
 * Interpolates a stop list at `position`. Both lists share this logic: outside
 * the first and last stop the value is held, which is what makes a gradient with
 * stops at 20% and 80% flat at both ends.
 */
function interpolate<T extends { position: number }>(
  stops: readonly T[],
  position: number,
  lerp: (a: T, b: T, t: number) => T,
): T {
  const first = stops[0];
  if (stops.length === 1) return first;
  if (position <= first.position) return first;
  const last = stops[stops.length - 1];
  if (position >= last.position) return last;

  for (let i = 1; i < stops.length; i++) {
    const b = stops[i];
    if (position > b.position) continue;
    const a = stops[i - 1];
    const span = b.position - a.position;
    return span <= 0 ? b : lerp(a, b, (position - a.position) / span);
  }
  return last;
}

export function sampleGradient(gradient: Gradient, position: number): RGBA {
  const t = clamp(position, 0, 1);
  const colorStops = sortStops(gradient.stops);
  const opacityStops = sortStops(gradient.opacityStops);
  const color = interpolate(colorStops, t, (a, b, k) => ({
    position: t,
    color: mixColor(a.color, b.color, k),
  })).color;
  const opacity =
    opacityStops.length === 0
      ? 1
      : interpolate(opacityStops, t, (a, b, k) => ({
          position: t,
          opacity: a.opacity + (b.opacity - a.opacity) * k,
        })).opacity;
  return { r: color.r, g: color.g, b: color.b, a: color.a * opacity };
}

/**
 * Bakes the gradient into an RGBA8 strip for the shader. Straight alpha, so the
 * gradient pass can composite it the same way a paint stroke composites.
 */
export function gradientToLut(gradient: Gradient, size = GRADIENT_LUT_SIZE): Uint8Array {
  const data = new Uint8Array(size * 4);
  const last = Math.max(1, size - 1);
  for (let i = 0; i < size; i++) {
    const color = sampleGradient(gradient, i / last);
    data[i * 4] = Math.round(clamp(color.r, 0, 1) * 255);
    data[i * 4 + 1] = Math.round(clamp(color.g, 0, 1) * 255);
    data[i * 4 + 2] = Math.round(clamp(color.b, 0, 1) * 255);
    data[i * 4 + 3] = Math.round(clamp(color.a, 0, 1) * 255);
  }
  return data;
}

export function reverseGradient(gradient: Gradient): Gradient {
  return {
    ...gradient,
    stops: gradient.stops.map((stop) => ({ ...stop, position: 1 - stop.position })).reverse(),
    opacityStops: gradient.opacityStops
      .map((stop) => ({ ...stop, position: 1 - stop.position }))
      .reverse(),
  };
}

/** CSS `linear-gradient` body, for previews in the editor and the option bar. */
export function gradientToCss(gradient: Gradient, steps = 16): string {
  const parts: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const color = sampleGradient(gradient, t);
    const rgb = [color.r, color.g, color.b].map((c) => Math.round(clamp(c, 0, 1) * 255));
    parts.push(`rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${color.a.toFixed(3)}) ${(t * 100).toFixed(1)}%`);
  }
  return parts.join(', ');
}

const OPAQUE: OpacityStop[] = [
  { position: 0, opacity: 1 },
  { position: 1, opacity: 1 },
];

/** The default preset: whatever the two colour swatches currently hold. */
export function foregroundToBackground(foreground: RGBA, background: RGBA): Gradient {
  return {
    id: 'gradient.fgToBg',
    name: 'Foreground to Background',
    stops: [
      { position: 0, color: foreground },
      { position: 1, color: background },
    ],
    opacityStops: [...OPAQUE],
  };
}

export function foregroundToTransparent(foreground: RGBA): Gradient {
  return {
    id: 'gradient.fgToTransparent',
    name: 'Foreground to Transparent',
    stops: [
      { position: 0, color: foreground },
      { position: 1, color: foreground },
    ],
    opacityStops: [
      { position: 0, opacity: 1 },
      { position: 1, opacity: 0 },
    ],
  };
}

function solid(id: string, name: string, from: RGBA, to: RGBA): Gradient {
  return {
    id,
    name,
    stops: [
      { position: 0, color: from },
      { position: 1, color: to },
    ],
    opacityStops: [...OPAQUE],
  };
}

const rgb = (r: number, g: number, b: number): RGBA => ({ r, g, b, a: 1 });

export const BUILTIN_GRADIENTS: Gradient[] = [
  solid('gradient.blackWhite', 'Black to White', rgb(0, 0, 0), rgb(1, 1, 1)),
  solid('gradient.whiteBlack', 'White to Black', rgb(1, 1, 1), rgb(0, 0, 0)),
  {
    id: 'gradient.transparentRainbow',
    name: 'Spectrum',
    stops: [
      { position: 0, color: rgb(1, 0, 0) },
      { position: 0.17, color: rgb(1, 1, 0) },
      { position: 0.34, color: rgb(0, 1, 0) },
      { position: 0.5, color: rgb(0, 1, 1) },
      { position: 0.67, color: rgb(0, 0, 1) },
      { position: 0.84, color: rgb(1, 0, 1) },
      { position: 1, color: rgb(1, 0, 0) },
    ],
    opacityStops: [...OPAQUE],
  },
  {
    id: 'gradient.sunset',
    name: 'Sunset',
    stops: [
      { position: 0, color: rgb(0.99, 0.76, 0.18) },
      { position: 0.45, color: rgb(0.95, 0.35, 0.24) },
      { position: 1, color: rgb(0.35, 0.11, 0.44) },
    ],
    opacityStops: [...OPAQUE],
  },
  {
    id: 'gradient.steel',
    name: 'Steel Bar',
    stops: [
      { position: 0, color: rgb(0.35, 0.36, 0.4) },
      { position: 0.25, color: rgb(0.92, 0.93, 0.96) },
      { position: 0.5, color: rgb(0.45, 0.47, 0.52) },
      { position: 0.75, color: rgb(0.86, 0.88, 0.92) },
      { position: 1, color: rgb(0.3, 0.31, 0.35) },
    ],
    opacityStops: [...OPAQUE],
  },
];

/** Keeps a stop list legal: sorted, in range and never empty. */
export function normaliseGradient(gradient: Gradient): Gradient {
  const stops = sortStops(gradient.stops).map((stop) => ({
    ...stop,
    position: clamp(stop.position, 0, 1),
  }));
  const opacityStops = sortStops(gradient.opacityStops).map((stop) => ({
    ...stop,
    position: clamp(stop.position, 0, 1),
    opacity: clamp(stop.opacity, 0, 1),
  }));
  return {
    ...gradient,
    stops: stops.length > 0 ? stops : [{ position: 0, color: { r: 0, g: 0, b: 0, a: 1 } }],
    opacityStops: opacityStops.length > 0 ? opacityStops : [...OPAQUE],
  };
}
